import { useContext, useMemo } from 'react';
import { GlobalPagePropsContext } from 'src/common/services/globalPagePropsContext';
import { getLocationState } from 'src/common/migration/NavigationData';
import { useLocation } from 'react-use';
import { useClientReady } from 'src/lib/SSR/ClientOnly/ClientOnly';

function useSearchQuery() {
  const location = useLocation();
  return useMemo(() => location.search, [location.search]);
}

function normalizeParams(
  rawParams: Record<string, undefined | string | string[]>,
): Record<string, undefined | string> {
  return Object.keys(rawParams).reduce((o, key) => {
    const value = rawParams[key];
    o[key] = value !== undefined ? (Array.isArray(value) ? value[0] : value) : undefined;
    return o;
  }, {} as Record<string, undefined | string>);
}

const emptySearchParams = {};

export function useSearchParams(normalize?: true): Record<string, undefined | string>;
// eslint-disable-next-line no-redeclare
export function useSearchParams(normalize: false): Record<string, undefined | string | string[]>;
// eslint-disable-next-line no-redeclare
export function useSearchParams(normalize = true): Record<string, undefined | string | string[]> {
  const pageProps = useContext(GlobalPagePropsContext);
  const clientReady = useClientReady();
  const clientSearchQuery = useSearchQuery();
  const clientSearchParams = useMemo(
    () => clientSearchQuery && getLocationState(clientSearchQuery),
    [clientSearchQuery],
  );
  const rawParams =
    (clientReady ? clientSearchParams : pageProps.location?.state) || emptySearchParams;
  return useMemo(
    () => (normalize ? normalizeParams(rawParams) : rawParams),
    [rawParams, normalize],
  );
}
